import {
  TAX_SETUP_SECTION,
  TaxSetupSectionId,
} from "v2/constants/taxSetupSections";

export type QuestionOption = { label: string; value: string };

export type FieldType =
  | "select"
  | "multiselect"
  | "text"
  | "textarea"
  | "number"
  | "month"
  | "file"
  | "entityList";

export const TAX_FIELD_TYPE = {
  FILE: "file",
  ENTITY_LIST: "entityList",
} as const satisfies Record<string, FieldType>;

export const PAYLOAD_TYPE = {
  VALUE: "value",
  NUMBER: "number",
  BOOL_YES: "boolYes",
  BOOL_NO: "boolNo",
  BOOL_SAME: "boolSame",
} as const;

export type PayloadType = (typeof PAYLOAD_TYPE)[keyof typeof PAYLOAD_TYPE];

export const YES_VALUE = "yes";
export const NO_VALUE = "no";
export const DIFFERENT_VALUE = "different";

export const BUSINESS_DESCRIPTION_ID = "business_description";

export const BUSINESS_DESCRIPTION_MIN_WORDS = 8;
export const BUSINESS_DESCRIPTION_TARGET_MAX_WORDS = 12;

export const BUSINESS_DESCRIPTION_MAX_CHARS = 150;

export const BUSINESS_DESCRIPTION_TOOLTIP =
  "A brief description of your company's main product or service. This is used for " +
  "state filings and compliance records. Example: \u201cCloud-based accounting software " +
  "for small businesses in the US.\u201d";

export const OWNS_OTHER_COMPANIES_ID = "owns_other_companies";
export const TAX_ONBOARDING_ENTITIES_KEY = "tax_onboarding_entities";

export const OPERATES_OTHER_STATES_ID = "operates_other_states";
export const HAS_US_EMPLOYEES_ID = "has_us_employees";
export const STATES_OPERATING_IN_US_KEY = "states_operating_in_us";
export const STATES_WITH_EMPLOYEES_KEY = "states_with_employees";

// These two payload keys distinguish "never answered" (omitted/null) from
// "answered, confirmed none" ([]) on the backend. The confirmed-empty answer
// only round-trips through the question's own Yes/No gate, so both directions
// of that conversion need to know which gate owns which payload key.
export const CONFIRMED_EMPTY_GATE_BY_KEY: Record<string, string> = {
  [STATES_OPERATING_IN_US_KEY]: OPERATES_OTHER_STATES_ID,
  [STATES_WITH_EMPLOYEES_KEY]: HAS_US_EMPLOYEES_ID,
};

export type TaxSetupQuestion = {
  id: string;
  label: string;
  tooltip?: string;

  isRequired?: boolean;

  section?: TaxSetupSectionId;

  type: "single" | "multi";

  fieldType?: FieldType;
  placeholder?: string;
  options?: QuestionOption[];

  optionsSource?: "usStates" | "countries";

  optionsFromParent?: boolean;

  includeHomeState?: boolean;

  payloadKey?: string;
  payloadType?: PayloadType;
  followUp?: {
    triggerValues?: string[];
    question: TaxSetupQuestion;
  };

  followUpGroups?: {
    triggerValues: string[];
    questions: TaxSetupQuestion[];
  }[];
};

export type RemoteFileValue = {
  uuid: string;
  name: string;
  thumbnail?: string;
  extension?: string;
};

export type TaxSetupValues = Record<
  string,
  string | string[] | RemoteFileValue
>;

export const ENTITY_TOKEN = "{{entity}}";
export const SEASON_TOKEN = "{{season}}";
export const HOME_STATE_TOKEN = "{{homeState}}";

const HOME_STATE_FALLBACK = "your home state";

export type QuestionTextContext = {
  entityName: string;
  season: number;
  homeState?: string;
};

export const interpolateQuestionText = (
  text: string,
  { entityName, season, homeState }: QuestionTextContext
): string =>
  text
    .split(ENTITY_TOKEN)
    .join(entityName)
    .split(SEASON_TOKEN)
    .join(String(season))
    .split(HOME_STATE_TOKEN)
    .join(homeState || HOME_STATE_FALLBACK);

// Shared so the current-season and past-season variants can never drift apart.
const FOREIGN_SHAREHOLDER_QUALIFIER =
  "(people who are not US tax residents, or entities not incorporated in the US)";

const FOREIGN_SHAREHOLDER_TOOLTIP =
  "A person counts as foreign only if they are not a US citizen, do not hold a green card, and do not spend much of the year in the US. A company counts as foreign based on where it is incorporated, not who owns it. Each foreign owner with 25% or more needs its own Form 5472. Missing even one means a $25,000 penalty.";

const YES_NO_UNSURE: QuestionOption[] = [
  { label: "Yes", value: YES_VALUE },
  { label: "No", value: NO_VALUE },
];

export const NO_SHAREHOLDERS_VALUE = "0";

export const TAX_SETUP_QUESTIONS: TaxSetupQuestion[] = [
  {
    id: BUSINESS_DESCRIPTION_ID,
    section: TAX_SETUP_SECTION.COMPANY_BASICS,
    label: `Describe what {{entity}} does in ${BUSINESS_DESCRIPTION_MIN_WORDS}-${BUSINESS_DESCRIPTION_TARGET_MAX_WORDS} words`,
    tooltip: BUSINESS_DESCRIPTION_TOOLTIP,
    type: "single",
    fieldType: "textarea",
    placeholder:
      "Cloud-based accounting software for small businesses in the US",
    payloadKey: "business_description",
    payloadType: PAYLOAD_TYPE.VALUE,
  },
  {
    id: "tax_year_end",
    section: TAX_SETUP_SECTION.COMPANY_BASICS,
    label: "When does {{entity}}' tax year end?",
    tooltip:
      "Your tax year is the 12-month period your company reports income for. Most US startups run January to December. If your year ends in a different month, your deadlines shift.",
    type: "single",
    fieldType: "select",
    placeholder: "Select",
    payloadKey: "tax_year_end",
    payloadType: PAYLOAD_TYPE.VALUE,
    options: [
      { label: "January 31", value: "1" },
      { label: "February 28", value: "2" },
      { label: "March 31", value: "3" },
      { label: "April 30", value: "4" },
      { label: "May 31", value: "5" },
      { label: "June 30", value: "6" },
      { label: "July 31", value: "7" },
      { label: "August 31", value: "8" },
      { label: "September 30", value: "9" },
      { label: "October 31", value: "10" },
      { label: "November 30", value: "11" },
      { label: "December 31", value: "12" },
    ],
  },
  {
    id: "filed_extension",
    section: TAX_SETUP_SECTION.COMPANY_BASICS,
    label:
      "Did you file an extension for {{entity}}' {{season}} federal return?",
    tooltip:
      "An extension pushes your federal filing deadline out by about six months. It doesn't give an extension on any tax you owe, but it buys you time to file the return. If you haven't filed one yet and your deadline is close, we'll add it to your cart.",
    type: "single",
    placeholder: "Select",

    payloadKey: "is_7004_required",
    payloadType: PAYLOAD_TYPE.BOOL_NO,
    options: [
      { label: "Yes, I filed one", value: YES_VALUE },
      { label: "No", value: NO_VALUE },
    ],

    followUp: {
      triggerValues: [YES_VALUE],
      question: {
        id: "extension_filed_copy",
        label: "Upload the filed copy",
        type: "single",
        fieldType: "file",
        placeholder: "Click or drop files here",
        payloadKey: "7004_filed_copy",
        payloadType: PAYLOAD_TYPE.VALUE,
      },
    },
  },
  {
    id: "number_of_shareholder",
    section: TAX_SETUP_SECTION.OWNERSHIP,
    label: `How many foreign shareholders ${FOREIGN_SHAREHOLDER_QUALIFIER} own 25% or more of ${ENTITY_TOKEN}?`,
    tooltip: FOREIGN_SHAREHOLDER_TOOLTIP,
    type: "single",
    placeholder: "Select",
    payloadKey: "number_of_shareholder",
    payloadType: PAYLOAD_TYPE.NUMBER,
    options: [
      { label: "1", value: "1" },
      { label: "2", value: "2" },
      { label: "3", value: "3" },
      { label: "4", value: "4" },
      { label: "None at 25%+", value: NO_SHAREHOLDERS_VALUE },
    ],
  },
  {
    id: OWNS_OTHER_COMPANIES_ID,
    section: TAX_SETUP_SECTION.OWNERSHIP,
    label: "Does {{entity}} own any other companies, in the US or abroad?",
    tooltip:
      "Owning other companies — especially outside the US — can mean extra filings (like Form 5471).",
    type: "single",
    placeholder: "Select",
    payloadKey: "owns_other_companies",
    payloadType: PAYLOAD_TYPE.VALUE,
    options: YES_NO_UNSURE,

    followUp: {
      triggerValues: [YES_VALUE],
      question: {
        id: "subsidiary_entities",
        label: "Add the companies you own",
        type: "single",
        fieldType: "entityList",
      },
    },
  },
  {
    id: OPERATES_OTHER_STATES_ID,
    section: TAX_SETUP_SECTION.OPERATIONS,
    label:
      "Besides {{homeState}}, did {{entity}} operate or hold offices in any other US state in {{season}}?",
    tooltip:
      "Each US state treats you as doing business there the moment you hire someone, rent space, or hold inventory in that state. That usually means a state tax return, an annual report, and sometimes registering as a “foreign” company (which just means “from another state,” not from another country).",
    type: "single",
    placeholder: "Select",
    options: YES_NO_UNSURE,
    followUp: {
      triggerValues: [YES_VALUE],
      question: {
        id: "operating_states",
        label:
          "Which states did {{entity}} operate in during {{season}}? Search and add any that apply",
        type: "multi",
        fieldType: "multiselect",
        placeholder: "Select",
        payloadKey: STATES_OPERATING_IN_US_KEY,
        payloadType: PAYLOAD_TYPE.VALUE,
        optionsSource: "usStates",
        followUp: {
          question: {
            id: "registered_business_states",
            label:
              "Out of these, which was {{entity}} already registered to do business in, in {{season}}?",
            type: "multi",
            fieldType: "multiselect",
            placeholder: "Select",
            payloadKey: "states_registered_in_us",
            payloadType: PAYLOAD_TYPE.VALUE,
            optionsFromParent: true,
          },
        },
      },
    },
  },
  {
    id: HAS_US_EMPLOYEES_ID,
    section: TAX_SETUP_SECTION.OPERATIONS,
    label: "Did {{entity}} have any employees on US payroll in {{season}}?",
    tooltip:
      "If you paid anyone as a W-2 employee (not a contractor), you have to be registered for payroll tax in every state they worked from, even if it's just one person working remotely.",
    type: "single",
    placeholder: "Select",
    options: YES_NO_UNSURE,
    followUp: {
      triggerValues: [YES_VALUE],
      question: {
        id: "employee_states",
        label: "Which states are your employees in?",
        type: "multi",
        fieldType: "multiselect",
        placeholder: "Select",
        payloadKey: STATES_WITH_EMPLOYEES_KEY,
        payloadType: PAYLOAD_TYPE.VALUE,
        optionsSource: "usStates",
        includeHomeState: true,
        followUp: {
          question: {
            id: "payroll_registered_states",
            label: "Which of these are you already registered for payroll in?",
            type: "multi",
            fieldType: "multiselect",
            placeholder: "Select",
            payloadKey: "states_with_us_payroll",
            payloadType: PAYLOAD_TYPE.VALUE,
            optionsFromParent: true,
          },
        },
      },
    },
  },
  {
    id: "paid_contractors",
    section: TAX_SETUP_SECTION.OPERATIONS,
    label:
      "In {{season}}, did {{entity}} pay any US-based contractor or freelancer $600 or more?",
    tooltip:
      "If you paid $600 or more in the year to any US-based contractor, freelancer, lawyer, LLC, LLP, or other business for services, you may owe a 1099-NEC by January 31.",
    type: "single",
    placeholder: "Select",
    payloadKey: "is_1099_required",
    payloadType: PAYLOAD_TYPE.BOOL_YES,
    options: YES_NO_UNSURE,
  },
  {
    id: "engineering_spend",
    section: TAX_SETUP_SECTION.FINANCES,
    label:
      "Did {{entity}} spend on engineering or product development in the US in {{season}}?",
    tooltip:
      "If you paid US engineers, developers, or scientists to build or improve a product, you may qualify for the R&D tax credit. For early-stage companies without much tax to offset, this credit can be used against payroll taxes (up to $500K a year), so it's a genuine cash benefit.",
    type: "single",
    placeholder: "Select",
    payloadKey: "is_rnd_required",
    payloadType: PAYLOAD_TYPE.BOOL_YES,
    options: YES_NO_UNSURE,
  },
  {
    id: "foreign_bank_accounts",
    section: TAX_SETUP_SECTION.FINANCES,
    label:
      "Did {{entity}} or its subsidiary hold non-US bank or financial accounts with a combined balance exceeding $10,000 at any time in {{season}}?",
    tooltip:
      "If your US company (or a subsidiary it owns more than 50% of) had signing authority over non-US bank accounts and the combined balances on those accounts exceeded $10,000 at any time during the year, you must file an FBAR. This also applies to multi-currency accounts your US entity holds, such as Wise or Mercury, with non-US balances.",
    type: "single",
    placeholder: "Select",
    payloadKey: "is_fbar_required",
    payloadType: PAYLOAD_TYPE.BOOL_YES,
    options: YES_NO_UNSURE,
  },
];

export const SAME_AS_CURRENT = "same";
export const MY_SAME_QUESTION_ID = "my_same";
export const SAME_AS_CURRENT_TAX_YEAR_KEY = "same_as_current_tax_year";

export const MY_SAME_QUESTION: TaxSetupQuestion = {
  id: MY_SAME_QUESTION_ID,
  label: "",
  type: "single",
  payloadKey: SAME_AS_CURRENT_TAX_YEAR_KEY,
  payloadType: PAYLOAD_TYPE.BOOL_SAME,
};

const buildDeltaQuestions = (
  season: number,
  currentSeason: number
): TaxSetupQuestion[] => {
  const sameOption: QuestionOption = {
    label: `Same as ${currentSeason}`,
    value: SAME_AS_CURRENT,
  };

  return [
    {
      id: "number_of_shareholder",
      label: `In ${season}, how many foreign shareholders ${FOREIGN_SHAREHOLDER_QUALIFIER} owned 25% or more of ${ENTITY_TOKEN}?`,
      tooltip: FOREIGN_SHAREHOLDER_TOOLTIP,
      type: "single",
      placeholder: "Select",
      payloadKey: "number_of_shareholder",
      payloadType: PAYLOAD_TYPE.NUMBER,
      options: [
        sameOption,
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "None at 25%+", value: NO_SHAREHOLDERS_VALUE },
      ],
    },
    {
      id: "foreign_bank_accounts",
      label: `In ${season}, did you hold non-US accounts over $10,000?`,
      type: "single",
      placeholder: "Select",
      payloadKey: "is_fbar_required",
      payloadType: PAYLOAD_TYPE.BOOL_YES,
      options: [
        sameOption,
        { label: "Yes", value: YES_VALUE },
        { label: "No", value: NO_VALUE },
      ],
    },
    {
      id: "engineering_spend",
      label: `In ${season}, did you spend on engineering or product development?`,
      type: "single",
      placeholder: "Select",
      payloadKey: "is_rnd_required",
      payloadType: PAYLOAD_TYPE.BOOL_YES,
      options: [
        sameOption,
        { label: "Yes", value: YES_VALUE },
        { label: "No", value: NO_VALUE },
      ],
    },
    {
      id: OPERATES_OTHER_STATES_ID,
      label: `Besides {{homeState}}, did {{entity}} operate or hold offices in any other US state in ${season}?`,
      type: "single",
      placeholder: "Select",
      options: [
        sameOption,
        { label: "Yes", value: YES_VALUE },
        { label: "No", value: NO_VALUE },
      ],
      followUp: {
        triggerValues: [YES_VALUE],
        question: {
          id: "operating_states",
          label: `Which states did {{entity}} operate in during ${season}? Search and add any that apply`,
          type: "multi",
          fieldType: "multiselect",
          placeholder: "Select",
          payloadKey: STATES_OPERATING_IN_US_KEY,
          payloadType: PAYLOAD_TYPE.VALUE,
          optionsSource: "usStates",
          followUp: {
            question: {
              id: "registered_business_states",
              label: `Out of these, which was {{entity}} already registered to do business in, in ${season}?`,
              type: "multi",
              fieldType: "multiselect",
              placeholder: "Select",
              payloadKey: "states_registered_in_us",
              payloadType: PAYLOAD_TYPE.VALUE,
              optionsFromParent: true,
            },
          },
        },
      },
    },
    {
      id: HAS_US_EMPLOYEES_ID,
      label: `Did {{entity}} have any employees on US payroll in ${season}?`,
      type: "single",
      placeholder: "Select",
      options: [
        sameOption,
        { label: "Yes", value: YES_VALUE },
        { label: "No", value: NO_VALUE },
      ],
      followUp: {
        triggerValues: [YES_VALUE],
        question: {
          id: "employee_states",
          label: `Which states are your employees in, in ${season}?`,
          type: "multi",
          fieldType: "multiselect",
          placeholder: "Select",
          payloadKey: STATES_WITH_EMPLOYEES_KEY,
          payloadType: PAYLOAD_TYPE.VALUE,
          optionsSource: "usStates",
          includeHomeState: true,
          followUp: {
            question: {
              id: "payroll_registered_states",
              label: `Which of these was {{entity}} already registered for payroll in, in ${season}?`,
              type: "multi",
              fieldType: "multiselect",
              placeholder: "Select",
              payloadKey: "states_with_us_payroll",
              payloadType: PAYLOAD_TYPE.VALUE,
              optionsFromParent: true,
            },
          },
        },
      },
    },
  ];
};

const LABEL_ONLY_SEASON = 0;

export const PAST_SEASON_DELTA_IDS = buildDeltaQuestions(
  LABEL_ONLY_SEASON,
  LABEL_ONLY_SEASON
)
  .filter(
    (question) =>
      question.payloadKey ||
      Object.values(CONFIRMED_EMPTY_GATE_BY_KEY).includes(question.id)
  )
  .map((question) => question.id);

export const buildMultiYearQuestions = (
  season: number,
  currentSeason: number
): TaxSetupQuestion[] => {
  const deltaQuestions = buildDeltaQuestions(season, currentSeason);

  return [
    {
      ...MY_SAME_QUESTION,
      label: `Was ${season} basically the same as ${currentSeason}?`,
      placeholder: "Select",
      options: [
        {
          label: `Same (file ${season} like ${currentSeason})`,
          value: SAME_AS_CURRENT,
        },
        { label: "Different", value: DIFFERENT_VALUE },
      ],
      followUpGroups: [
        { triggerValues: [DIFFERENT_VALUE], questions: deltaQuestions },
      ],
    },
  ];
};

export const collectQuestions = (
  questions: TaxSetupQuestion[]
): TaxSetupQuestion[] => {
  return questions.flatMap((question) => {
    const followUps = question.followUp
      ? collectQuestions([question.followUp.question])
      : [];

    const groupQuestions = (question.followUpGroups ?? []).flatMap((group) =>
      collectQuestions(group.questions)
    );

    return [question, ...followUps, ...groupQuestions];
  });
};

export const buildTaxSetupInitialValues = (
  questions: TaxSetupQuestion[]
): TaxSetupValues => {
  return Object.fromEntries(
    collectQuestions(questions).map((question) => [
      question.id,
      question.type === "multi" ? [] : "",
    ])
  );
};

export const hasAnyAnswer = (values: TaxSetupValues): boolean => {
  return Object.values(values).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== ""
  );
};

export const REQUIRED_QUESTION_IDS = TAX_SETUP_QUESTIONS.filter(
  (question) => question.isRequired
).map((question) => question.id);
